import React from 'react';
import { motion } from 'framer-motion';
import { DollarSign, TrendingUp, PiggyBank, CreditCard, Shield, Calculator, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';

const FinancialEducationPage = () => {
  const navigate = useNavigate();

  const courses = [
    {
      icon: PiggyBank,
      title: 'Budgeting Fundamentals',
      description: 'Learn to create and maintain a budget that works for your lifestyle and financial goals.',
      topics: ['Income tracking', 'Expense categorization', '50/30/20 rule', 'Emergency funds'],
    },
    {
      icon: CreditCard,
      title: 'Credit Building & Repair',
      description: 'Understand credit scores, repair damaged credit, and build a strong financial foundation.',
      topics: ['Credit score factors', 'Dispute processes', 'Credit utilization', 'Building history'],
    },
    {
      icon: TrendingUp,
      title: 'Investing Basics',
      description: 'Introduction to investing concepts, from savings accounts to stocks and retirement planning.',
      topics: ['Compound interest', 'Risk tolerance', '401(k) & IRAs', 'Diversification'],
    },
    {
      icon: Shield,
      title: 'Financial Protection',
      description: 'Safeguard your finances with proper insurance, fraud prevention, and estate planning basics.',
      topics: ['Insurance types', 'Identity protection', 'Emergency planning', 'Basic estate planning'],
    },
  ];

  const workshops = [
    {
      title: 'First-Time Homebuyer Workshop',
      description: 'Everything you need to know about purchasing your first home, from saving for a down payment to closing.',
      duration: '4-week course',
    },
    {
      title: 'Debt Freedom Strategy',
      description: 'Proven strategies to eliminate debt and achieve financial independence.',
      duration: '6-week course',
    },
    {
      title: 'Small Business Finances',
      description: 'Financial management fundamentals for entrepreneurs and small business owners.',
      duration: '8-week course',
    },
  ];

  return (
    <div className="min-h-screen bg-white pt-24" data-testid="financial-education-page">
      {/* Hero Section */}
      <section className="py-16 sm:py-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
          <motion.div
            className="max-w-3xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#1E3A8A] mb-4">
              Our Programs
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium text-[#0B1120] tracking-tight leading-tight mb-6">
              Financial Education
            </h1>
            <p className="text-xl text-slate-600 font-light leading-relaxed mb-8">
              Knowledge is power, especially when it comes to your finances. Our comprehensive 
              financial literacy programs equip individuals and families with the tools they need 
              to achieve financial stability and build generational wealth.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <div
                className="bg-[#1E3A8A]/10 border border-[#1E3A8A]/30 text-[#1E3A8A] px-8 py-4 text-lg rounded-full inline-flex items-center font-medium"
                data-testid="financial-enroll-btn"
              >
                New courses coming soon . June 2026
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Core Courses */}
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-medium text-[#0B1120] mb-4">
              Core Financial Courses
            </h2>
            <p className="text-lg text-slate-600 font-light max-w-2xl mx-auto">
              Free educational programs designed to build financial confidence from the ground up.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {courses.map((course, idx) => (
              <motion.div
                key={course.title}
                className="p-8 rounded-2xl border border-slate-200 hover:border-[#1E3A8A]/30 hover:shadow-lg transition-all"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
              >
                <div className="w-14 h-14 rounded-xl bg-[#DBEAFE] flex items-center justify-center mb-6">
                  <course.icon className="w-7 h-7 text-[#1E3A8A]" />
                </div>
                <h3 className="text-xl font-semibold text-slate-800 mb-3 font-serif">
                  {course.title}
                </h3>
                <p className="text-slate-600 font-light mb-4">
                  {course.description}
                </p>
                <div className="flex flex-wrap gap-2">
                  {course.topics.map((topic) => (
                    <span
                      key={topic}
                      className="px-3 py-1 bg-slate-100 text-slate-600 text-sm rounded-full"
                    >
                      {topic}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Specialized Workshops */}
      <section className="py-16 sm:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-medium text-[#0B1120] mb-4">
              Specialized Workshops
            </h2>
            <p className="text-lg text-slate-600 font-light max-w-2xl mx-auto">
              In-depth programs for specific financial goals and life stages.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {workshops.map((workshop, idx) => (
              <motion.div
                key={workshop.title}
                className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
              >
                <span className="inline-block px-3 py-1 bg-[#DBEAFE] text-[#1E3A8A] text-sm font-medium rounded-full mb-4">
                  {workshop.duration}
                </span>
                <h3 className="text-xl font-semibold text-slate-800 mb-3 font-serif">
                  {workshop.title}
                </h3>
                <p className="text-slate-600 font-light">
                  {workshop.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { number: '2,500+', label: 'Students Enrolled' },
              { number: '$1.2M', label: 'Debt Eliminated' },
              { number: '85%', label: 'Credit Score Improvement' },
              { number: '150+', label: 'Home Purchases Assisted' },
            ].map((stat, idx) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
              >
                <p className="text-4xl font-bold text-[#1E3A8A] font-serif mb-2">{stat.number}</p>
                <p className="text-sm text-slate-500 uppercase tracking-wider">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-24 bg-[#0B1120] text-white">
        <div className="max-w-4xl mx-auto px-6 sm:px-12 lg:px-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-medium mb-6">
              Take Control of Your Financial Future
            </h2>
            <p className="text-lg text-slate-300 font-light mb-8">
              All our financial education programs are completely free. Start your journey to 
              financial freedom today.
            </p>
            <Button
              onClick={() => { navigate('/'); setTimeout(() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' }), 100); }}
              className="bg-white text-[#1E3A8A] hover:bg-slate-100 px-10 py-6 text-lg rounded-full"
            >
              Sign Up for Free
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default FinancialEducationPage;
